import React, { useState, useEffect } from "react";
import { Target, Lightbulb, Shield, Bot, Play } from "lucide-react";
import img1 from "../../../Assets/Images/VedioImg.jpg";

export default function FeaturesShowcaseSection() {
  const [isVisible, setIsVisible] = useState(false);
  const [isPlaying, setIsPlaying] = useState(false);

  useEffect(() => {
    setIsVisible(true);
  }, []);

  const features = [
    {
      icon: Target,
      title: "Digital System Development",
      description:
        "Custom-built platforms align with your unique operational workflows and business requirements",
      position: "top-left",
      color: "from-purple-500 to-purple-600",
      iconBg: "bg-purple-100",
      iconColor: "text-purple-600",
    },
    {
      icon: Lightbulb,
      title: "Engineering Architecture",
      description:
        "Systems engineered for scalability, high performance, and long-term operational efficiency.",
      position: "top-right",
      color: "from-purple-500 to-purple-600",
      iconBg: "bg-purple-100",
      iconColor: "text-purple-600",
    },
    {
      icon: Shield,
      title: "Cloud & VDI Infrastructure",
      description:
        "Secure and scalable environments built to support collaboration and productivity for distributed teams across locations.",
      position: "bottom-left",
      color: "from-purple-500 to-purple-600",
      iconBg: "bg-purple-100",
      iconColor: "text-purple-600",
    },
    {
      icon: Bot,
      title: "Data & Process Automation",
      description:
        "Streamlining  workflows through intelligent, accuracy, and decision-making across operations.",
      position: "bottom-right",
      color: "from-purple-500 to-purple-600",
      iconBg: "bg-purple-100",
      iconColor: "text-purple-600",
    },
  ];

  const getPositionClasses = (position) => {
    switch (position) {
      case "top-left":
        return "lg:col-start-1 lg:row-start-1";
      case "top-right":
        return "lg:col-start-3 lg:row-start-1";
      case "bottom-left":
        return "lg:col-start-1 lg:row-start-2";
      case "bottom-right":
        return "lg:col-start-3 lg:row-start-2";
      default:
        return "";
    }
  };

  const getDelayClass = (index) => {
    return `delay-${(index + 1) * 200}`;
  };

  return (
    <div className="bg-gradient-to-br from-slate-50 to-slate-100 py-12 px-6 md:px-8 lg:px-12">
      <div className="container mx-auto max-w-7xl">
        <div className="grid lg:grid-cols-3 lg:grid-rows-2 gap-8 items-center">
          {features.map((feature, index) => {
            const Icon = feature.icon;
            const positionClass = getPositionClasses(feature.position);
            const delay = index * 200;

            return (
              <div
                key={feature.title}
                className={`${positionClass} transition-all duration-1000 ${
                  isVisible
                    ? "opacity-100 translate-y-0"
                    : "opacity-0 translate-y-12"
                }`}
                style={{ transitionDelay: `${delay}ms` }}
              >
                <div className="group hover:scale-105 transition-all duration-300">
                  {/* Feature Card */}
                  <div className="space-y-6">
                    {/* Icon and Title */}
                    <div className="flex  gap-4">
                      <div
                        className={`flex-shrink-0 w-16 h-16 ${feature.iconBg} rounded-2xl flex items-center justify-center shadow-lg group-hover:shadow-xl transition-all duration-300`}
                      >
                        <Icon className={`w-8 h-8 ${feature.iconColor}`} />
                      </div>
                      <div className=" space-y-2">
                        <div className="flex gap-2">
                          <h3 className="text-base sm:text-lg font-bold text-slate-900 tracking-wide uppercase">
                            {feature.title}
                          </h3>
                          <div className="text-purple-400">
                            <svg
                              width="16"
                              height="16"
                              viewBox="0 0 16 16"
                              fill="currentColor"
                            >
                              <path d="M8 0l1.5 5.5L15 8l-5.5 1.5L8 15l-1.5-5.5L1 8l5.5-1.5z" />
                            </svg>
                          </div>
                        </div>
                        {/* Description */}
                        <p className="text-slate-600 text-sm sm:text-base leading-relaxed ">
                          {feature.description}
                        </p>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            );
          })}

          {/* Center Circle - Video/Image */}
          <div
            className="lg:col-start-2 lg:row-start-1 lg:row-span-2 flex items-center justify-center transition-all duration-1000 delay-500"
            style={{
              opacity: isVisible ? 1 : 0,
              transform: isVisible ? "scale(1)" : "scale(0.8)",
            }}
          >
            <div className="relative group">
              {/* Main Circle */}
              <div className="size-80 md:w-96 md:h-96 rounded-full overflow-hidden shadow-2xl relative">
                {/* Placeholder Image */}
                <div className="absolute inset-0 bg-gradient-to-br from-slate-300 to-slate-400">
                  <div>
                    <img src={img1} alt="" className="rounded-full" />
                  </div>
                </div>

                {/* Overlay */}
                <div className="absolute inset-0 bg-gradient-to-t from-slate-900/40 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300"></div>
              </div>

              {/* Decorative Blur Circles */}
              <div className="absolute inset-0 -z-10">
                <div className="absolute top-0 left-0 w-64 h-64 bg-purple-400/20 rounded-full blur-3xl animate-pulse"></div>
                <div
                  className="absolute bottom-0 right-0 w-64 h-64 bg-blue-400/20 rounded-full blur-3xl animate-pulse"
                  style={{ animationDelay: "1s" }}
                ></div>
              </div>

              {/* Ring Animation */}
              <div className="absolute inset-0 rounded-full border-4 border-purple-400/30 animate-ping-slow"></div>
            </div>
          </div>
        </div>
      </div>

      <style>{`
        @keyframes ping-slow {
          0% {
            transform: scale(1);
            opacity: 0.5;
          }
          50% {
            transform: scale(1.05);
            opacity: 0.3;
          }
          100% {
            transform: scale(1);
            opacity: 0.5;
          }
        }

        .animate-ping-slow {
          animation: ping-slow 3s cubic-bezier(0.4, 0, 0.6, 1) infinite;
        }
      `}</style>
    </div>
  );
}
